import { useEffect } from 'react'
import MarketingNav from '../../components/marketing/MarketingNav'
import MarketingFooter from '../../components/marketing/MarketingFooter'

const font = "'Geist', 'Geist Variable', ui-sans-serif, system-ui, sans-serif"

const tocItems = [
  { id: 'who-this-policy-applies-to', label: 'Who This Policy Applies To' },
  { id: 'information-we-collect', label: 'Information We Collect' },
  { id: 'how-we-use-your-information', label: 'How We Use Your Information' },
  { id: 'third-party-integrations-and-connected-services', label: 'Third-Party Integrations and Connected Services' },
  { id: 'how-we-share-your-information', label: 'How We Share Your Information' },
  { id: 'how-we-keep-your-information-secure', label: 'How We Keep Your Information Secure' },
  { id: 'data-retention', label: 'Data Retention' },
  { id: 'your-privacy-rights', label: 'Your Privacy Rights' },
  { id: 'childrens-privacy', label: "Children's Privacy" },
  { id: 'google-api-services-user-data-policy', label: 'Google API Services User Data Policy' },
  { id: 'international-data-transfers', label: 'International Data Transfers' },
  { id: 'california-consumer-privacy-act-ccpa', label: 'California Consumer Privacy Act (CCPA)' },
  { id: 'changes-to-this-privacy-policy', label: 'Changes to This Privacy Policy' },
  { id: 'how-to-contact-us', label: 'How to Contact Us' },
] as const

const integrationRows = [
  {
    integration: 'Google Drive',
    access: 'Files and folders you select; file metadata',
    noAccess: 'Files you do not select; Google Photos; Google Meet recordings',
  },
  {
    integration: 'Notion',
    access: 'Pages and databases you select',
    noAccess: 'Pages you have not shared with the integration',
  },
  {
    integration: 'Dropbox',
    access: 'Files you select and import',
    noAccess: 'Files you do not select',
  },
  {
    integration: 'Gmail',
    access: 'Email attachments you choose to import',
    noAccess: 'Email body content, contacts, your full inbox',
  },
  {
    integration: 'Slack',
    access: 'Files and documents you choose to import',
    noAccess: 'Message history, DMs, channels',
  },
] as const

function Section({
  id,
  number,
  title,
  children,
}: {
  id: string
  number: number
  title: string
  children: React.ReactNode
}) {
  return (
    <section id={id} style={{ display: 'flex', flexDirection: 'column', gap: 18, scrollMarginTop: 96 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#B15B33',
            fontFamily: font,
          }}
        >
          Section {number}
        </span>
        <h2
          style={{
            margin: 0,
            fontSize: 'clamp(26px, 3vw, 34px)',
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            color: '#1A1714',
            fontFamily: font,
          }}
        >
          {number}. {title}
        </h2>
      </div>
      {children}
    </section>
  )
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        fontSize: 16,
        lineHeight: 1.75,
        color: '#3B3128',
        fontFamily: font,
      }}
    >
      {children}
    </p>
  )
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul
      style={{
        margin: 0,
        paddingLeft: 22,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        color: '#3B3128',
      }}
    >
      {items.map((item, index) => (
        <li
          key={index}
          style={{
            fontSize: 16,
            lineHeight: 1.75,
            color: '#3B3128',
            fontFamily: font,
          }}
        >
          {item}
        </li>
      ))}
    </ul>
  )
}

function Subheading({ children }: { children: React.ReactNode }) {
  return (
    <h3
      style={{
        margin: 0,
        fontSize: 18,
        lineHeight: 1.35,
        letterSpacing: '-0.02em',
        color: '#1A1714',
        fontFamily: font,
      }}
    >
      {children}
    </h3>
  )
}

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
    document.title = 'Corner Privacy Policy'
  }, [])

  return (
    <div
      style={{
        background: '#F2EDE6',
        minHeight: '100vh',
        fontFamily: font,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <MarketingNav />

      <main
        style={{
          width: '100%',
          maxWidth: 1440,
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingTop: 112,
          paddingRight: 'clamp(20px, 6vw, 80px)',
          paddingBottom: 96,
          paddingLeft: 'clamp(20px, 6vw, 80px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 920,
            display: 'flex',
            flexDirection: 'column',
            gap: 56,
          }}
        >
          <section
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
              paddingTop: 24,
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                width: 'fit-content',
                padding: '6px 14px',
                borderRadius: 999,
                background: '#FDF0EA',
                border: '1px solid #F0D5C4',
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#D46A3A',
                  fontFamily: font,
                }}
              >
                Legal
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'clamp(40px, 6vw, 64px)',
                  lineHeight: 0.98,
                  letterSpacing: '-0.05em',
                  color: '#1A1714',
                  fontFamily: font,
                  maxWidth: 760,
                }}
              >
                Corner Privacy Policy
              </h1>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                  color: '#8B7D6B',
                  fontFamily: font,
                }}
              >
                Last updated: March 31, 2026
              </span>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                padding: '24px clamp(20px, 4vw, 32px)',
                borderRadius: 24,
                background: '#FBF7F2',
                border: '1px solid #E4DDD4',
              }}
            >
              <Paragraph>
                Corner (&quot;Corner,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is operated by
                Boltzman AI LLC, a New Jersey limited liability company located at 150 Lexington Avenue,
                Linden, NJ 07036. We respect your right to privacy and are dedicated to protecting any
                information we have about you. This Privacy Policy describes how we collect, use, and
                share information when you use Corner&apos;s website at trycorner.co and the Corner platform
                and services (collectively, the &quot;Services&quot;).
              </Paragraph>
              <Paragraph>
                By using the Services, you agree to the collection and use of your information as
                described in this Privacy Policy. If you do not agree with the terms of this Privacy
                Policy, please do not use the Services.
              </Paragraph>
            </div>
          </section>

          <section
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
              padding: '28px clamp(20px, 4vw, 32px)',
              borderRadius: 24,
              background: '#FFFFFF',
              border: '1px solid #E4DDD4',
            }}
          >
            <Subheading>Table of Contents</Subheading>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 12,
              }}
            >
              {tocItems.map((item, index) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  style={{
                    display: 'block',
                    padding: '12px 14px',
                    borderRadius: 16,
                    textDecoration: 'none',
                    color: '#3B3128',
                    background: '#F8F3EC',
                    border: '1px solid #EDE3D8',
                    fontSize: 14,
                    lineHeight: 1.5,
                    fontFamily: font,
                  }}
                >
                  {index + 1}. {item.label}
                </a>
              ))}
            </div>
          </section>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 44 }}>
            <Section id="who-this-policy-applies-to" number={1} title="Who This Policy Applies To">
              <Paragraph>
                This Privacy Policy applies to all users of Corner&apos;s website and Services, including
                individuals who create a Corner account, connect third-party integrations, or otherwise
                interact with us.
              </Paragraph>
              <Paragraph>
                Corner&apos;s Services are designed for professional use by individuals and businesses,
                including real estate agents, legal professionals, medical offices, founders, operators,
                and researchers who work with documents.
              </Paragraph>
              <Paragraph>
                This Privacy Policy governs the personal information for which Boltzman AI LLC is the data
                controller. When you upload, import, or process documents through Corner, that document
                content (&quot;Customer Content&quot;) is processed on your behalf and remains under your control.
                We process Customer Content as a data processor at your direction.
              </Paragraph>
            </Section>

            <Section id="information-we-collect" number={2} title="Information We Collect">
              <Paragraph>We collect information in several ways:</Paragraph>

              <Subheading>A. Information You Provide Directly</Subheading>
              <BulletList
                items={[
                  <>
                    <strong>Account Information:</strong> When you create a Corner account, we collect
                    your name, email address, password, and any optional profile information you choose
                    to provide. If you subscribe to a paid plan, we also collect billing and payment
                    information processed through our third-party payment processor.
                  </>,
                  <>
                    <strong>Communications:</strong> If you contact us for support, respond to a survey,
                    or communicate with us through any channel, we collect your name, email address, the
                    contents of your messages, and any other information you choose to share.
                  </>,
                ]}
              />

              <Subheading>B. Information Collected Automatically</Subheading>
              <BulletList
                items={[
                  <>
                    <strong>Log Data:</strong> When you use the Services, our servers automatically
                    record information including your IP address, browser type and version, operating
                    system, pages visited, date and time of access, and referring URLs.
                  </>,
                  <>
                    <strong>Usage Data:</strong> We collect information about how you interact with the
                    Services, including features you use, documents you upload and process, integrations
                    you connect, the volume and type of documents processed, time spent in the workspace,
                    and other usage patterns.
                  </>,
                  <>
                    <strong>Device Information:</strong> We collect information about the device you use
                    to access the Services, including device identifiers, operating system, and browser
                    type.
                  </>,
                  <>
                    <strong>Cookies and Similar Technologies:</strong> We use cookies and similar tracking
                    technologies to manage your session, remember your preferences, and analyze how the
                    Services are used. You may configure your browser to refuse cookies, but this may
                    affect the functionality of the Services.
                  </>,
                ]}
              />

              <Subheading>C. Information from Third-Party Integrations</Subheading>
              <Paragraph>
                When you connect Corner to third-party services such as Notion, Google Drive, Dropbox,
                Slack, Gmail, or other supported integrations, we receive information through those
                connections including:
              </Paragraph>
              <BulletList
                items={[
                  'Authentication credentials and OAuth tokens (stored securely, never in plain text)',
                  'Metadata about files and documents you select to import (file names, types, sizes, folder structure)',
                  'The content of files and documents you explicitly choose to import into Corner for processing',
                  'Basic workspace or account information from the connected service (e.g., workspace name, user ID)',
                ]}
              />
              <Paragraph>
                We only access third-party service data that you explicitly authorize and select. We do
                not passively scan, index, or monitor your connected accounts. Access is limited to what
                you choose to bring into Corner.
              </Paragraph>

              <Subheading>D. Customer Content</Subheading>
              <Paragraph>
                Customer Content means any files, documents, text, audio, images, or other materials you
                upload, import, or create within Corner. This includes PDFs, Word documents, spreadsheets,
                scanned images, audio recordings submitted for transcription, and pasted text.
              </Paragraph>
              <Paragraph>
                You retain ownership of all Customer Content. We process Customer Content solely to
                provide the Services to you and as described in this Privacy Policy.
              </Paragraph>
            </Section>

            <Section id="how-we-use-your-information" number={3} title="How We Use Your Information">
              <Paragraph>We use the information we collect for the following purposes:</Paragraph>
              <BulletList
                items={[
                  'To provide and operate the Services, including processing documents, generating AI-powered summaries, enabling chat with documents, performing file conversion, enabling e-signatures, and all other core features of Corner',
                  'To authenticate your identity and manage your account',
                  'To process connected integrations, including reading files from services you connect (e.g., importing a PDF from Google Drive) and writing outputs back to services at your direction (e.g., saving a summary to Notion)',
                  'To bill for paid Services and manage your subscription',
                  'To provide customer support and respond to your requests and inquiries',
                  'To improve and develop the Services, including analyzing usage patterns, diagnosing bugs, and building new features',
                  'To send transactional communications, including account confirmations, billing notices, security alerts, and policy updates',
                  'To send marketing communications about Corner features, updates, and offers - you may opt out at any time',
                  'To detect and prevent fraud, abuse, security threats, and violations of our Terms of Service',
                  'To comply with legal obligations, including responding to lawful requests from regulators or law enforcement',
                ]}
              />
              <Paragraph>
                We do not use your Customer Content to train our AI models or the AI models of any third
                party without your explicit consent.
              </Paragraph>
              <Paragraph>
                We may use de-identified or aggregated information, from which you cannot reasonably be
                identified, for research, analytics, and product improvement purposes.
              </Paragraph>
            </Section>

            <Section
              id="third-party-integrations-and-connected-services"
              number={4}
              title="Third-Party Integrations and Connected Services"
            >
              <Paragraph>
                Corner&apos;s core value is serving as the document workspace at the center of your existing
                workflow. To do this, Corner connects with third-party services through official, secure
                OAuth 2.0 integrations.
              </Paragraph>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Subheading>What we access</Subheading>
                <Paragraph>
                  When you authorize a third-party integration, Corner accesses only what is necessary to
                  provide the feature you are using:
                </Paragraph>
              </div>

              <div
                style={{
                  overflowX: 'auto',
                  borderRadius: 20,
                  border: '1px solid #E4DDD4',
                  background: '#FFFFFF',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    minWidth: 720,
                    borderCollapse: 'collapse',
                    fontFamily: font,
                  }}
                >
                  <thead>
                    <tr style={{ background: '#F8F3EC' }}>
                      <th style={tableHeadingStyle}>Integration</th>
                      <th style={tableHeadingStyle}>What we access</th>
                      <th style={tableHeadingStyle}>What we do NOT access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integrationRows.map((row) => (
                      <tr key={row.integration}>
                        <td style={tableCellPrimaryStyle}>{row.integration}</td>
                        <td style={tableCellStyle}>{row.access}</td>
                        <td style={tableCellStyle}>{row.noAccess}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Subheading>How tokens are stored</Subheading>
                <Paragraph>
                  OAuth access tokens and refresh tokens from connected services are stored encrypted in
                  our database. They are never stored in plain text, never committed to source control,
                  and are accessible only to the Corner application to make authorized API requests on
                  your behalf.
                </Paragraph>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Subheading>Revoking access</Subheading>
                <Paragraph>
                  You can disconnect any integration at any time from your Corner account settings. Upon
                  disconnection, we immediately stop accessing the connected service. Previously imported
                  documents remain in your Corner workspace unless you delete them.
                </Paragraph>
                <Paragraph>
                  When you revoke access, Corner&apos;s permissions are also removed from the third-party
                  platform. For full removal, you may also revoke Corner&apos;s access directly within the
                  settings of each third-party service.
                </Paragraph>
              </div>
            </Section>

            <Section id="how-we-share-your-information" number={5} title="How We Share Your Information">
              <Paragraph>We do not sell your personal information or Customer Content to third parties.</Paragraph>
              <Paragraph>We share information only in the following circumstances:</Paragraph>
              <BulletList
                items={[
                  <>
                    <strong>Service Providers:</strong> We work with third-party companies that help us
                    operate and deliver the Services. These include cloud hosting providers, payment
                    processors, authentication providers, email delivery services, AI model providers, and
                    analytics tools. These providers process data only as necessary to perform services on
                    our behalf and are contractually prohibited from using your data for their own
                    purposes.
                  </>,
                  <>
                    <strong>AI Model Providers:</strong> Corner uses third-party AI models to power
                    features such as document summarization, chat, and text extraction. When you process a
                    document using these features, the relevant content is sent to the AI model provider
                    under a data processing agreement. We select providers that do not use customer data
                    to train their models.
                  </>,
                  <>
                    <strong>Business Reorganization:</strong> If Boltzman AI LLC is involved in a merger,
                    acquisition, asset sale, or similar transaction, your information may be transferred
                    as part of that transaction. We will notify you before your information is transferred
                    and becomes subject to a different privacy policy.
                  </>,
                  <>
                    <strong>Legal Compliance:</strong> We may disclose your information if required to do
                    so by law, court order, or government request, or where we believe in good faith that
                    disclosure is necessary to protect the rights, property, or safety of Corner, our
                    users, or the public.
                  </>,
                  <>
                    <strong>With Your Consent:</strong> We may share your information for any other
                    purpose with your explicit consent.
                  </>,
                ]}
              />
            </Section>

            <Section id="how-we-keep-your-information-secure" number={6} title="How We Keep Your Information Secure">
              <Paragraph>
                We implement administrative, technical, and physical safeguards designed to protect your
                personal information and Customer Content against unauthorized access, loss, alteration,
                disclosure, or destruction.
              </Paragraph>
              <Paragraph>Security measures include:</Paragraph>
              <BulletList
                items={[
                  'Encryption of data in transit using TLS 1.2 or higher',
                  'Encryption of sensitive credentials and tokens at rest',
                  'Secure, access-controlled cloud infrastructure',
                  'Regular security reviews and updates',
                  'Employee access controls and confidentiality obligations',
                ]}
              />
              <Paragraph>
                Despite these measures, no system is completely secure. We cannot guarantee the absolute
                security of your information. If you believe your account has been compromised, contact us
                immediately at privacy@trycorner.co.
              </Paragraph>
            </Section>

            <Section id="data-retention" number={7} title="Data Retention">
              <Paragraph>
                We retain your personal information and Customer Content for as long as your account is
                active or as needed to provide the Services.
              </Paragraph>
              <Paragraph>
                If you delete your account, we will delete or anonymize your personal information and
                Customer Content within 30 days, except where we are required to retain it to comply with
                legal obligations, resolve disputes, or enforce our agreements.
              </Paragraph>
              <Paragraph>
                Log data and usage analytics may be retained in anonymized or aggregated form for longer
                periods for product analysis purposes.
              </Paragraph>
              <Paragraph>
                You may request deletion of your data at any time by contacting us at privacy@trycorner.co.
              </Paragraph>
            </Section>

            <Section id="your-privacy-rights" number={8} title="Your Privacy Rights">
              <Paragraph>
                Depending on your location and applicable law, you may have the following rights with
                respect to your personal information:
              </Paragraph>
              <BulletList
                items={[
                  <>
                    <strong>Access:</strong> Request a copy of the personal information we hold about you
                  </>,
                  <>
                    <strong>Correction:</strong> Request that we correct inaccurate or incomplete
                    information
                  </>,
                  <>
                    <strong>Deletion:</strong> Request that we delete your personal information, subject
                    to certain legal exceptions
                  </>,
                  <>
                    <strong>Restriction:</strong> Request that we restrict processing of your information
                    in certain circumstances
                  </>,
                  <>
                    <strong>Portability:</strong> Request that we provide your personal information in a
                    structured, machine-readable format
                  </>,
                  <>
                    <strong>Objection:</strong> Object to our processing of your information for direct
                    marketing or other purposes based on legitimate interest
                  </>,
                  <>
                    <strong>Withdraw Consent:</strong> Where processing is based on consent, withdraw that
                    consent at any time without affecting the lawfulness of prior processing
                  </>,
                  <>
                    <strong>Opt Out of Marketing:</strong> Unsubscribe from marketing emails at any time
                    by clicking the unsubscribe link in any email we send
                  </>,
                ]}
              />
              <Paragraph>
                To exercise any of these rights, contact us at privacy@trycorner.co. We will respond to
                all valid requests within the timeframe required by applicable law (typically within 30
                days). We may need to verify your identity before fulfilling your request.
              </Paragraph>
            </Section>

            <Section id="childrens-privacy" number={9} title="Children's Privacy">
              <Paragraph>
                Corner&apos;s Services are not directed to or intended for use by individuals under the age
                of 18. We do not knowingly collect personal information from anyone under 18. If you have
                reason to believe that a minor has provided us with personal information, please contact
                us at privacy@trycorner.co and we will take steps to delete that information promptly.
              </Paragraph>
            </Section>

            <Section
              id="google-api-services-user-data-policy"
              number={10}
              title="Google API Services User Data Policy"
            >
              <Paragraph>
                Corner&apos;s use of information received from Google APIs adheres to the Google API Services
                User Data Policy, including the Limited Use requirements.
              </Paragraph>
              <Paragraph>Specifically:</Paragraph>
              <BulletList
                items={[
                  'Corner only requests Google API scopes that are necessary to provide the features you use',
                  'We only access Google Drive files and metadata that you explicitly select and authorize',
                  'We do not share Google user data with third parties except as necessary to provide the Services, or as required by law',
                  'We do not use Google user data for advertising purposes',
                  'We do not allow humans to read your Google user data unless you have given explicit permission, it is necessary for security purposes, or it is required by law',
                  'We do not use or transfer Google user data for AI/ML model training',
                ]}
              />
              <Paragraph>
                For more information, visit the Google API Services User Data Policy.
              </Paragraph>
            </Section>

            <Section id="international-data-transfers" number={11} title="International Data Transfers">
              <Paragraph>
                Corner is operated from the United States. If you access the Services from outside the
                United States, your information may be transferred to and processed in the United States
                or other countries where our service providers operate.
              </Paragraph>
              <Paragraph>
                We take appropriate steps to ensure that any international transfers of personal data are
                carried out in compliance with applicable data protection laws, including through the use
                of standard contractual clauses or other lawful transfer mechanisms where required.
              </Paragraph>
            </Section>

            <Section
              id="california-consumer-privacy-act-ccpa"
              number={12}
              title="California Consumer Privacy Act (CCPA)"
            >
              <Paragraph>
                If you are a California resident, you have additional rights under the California Consumer
                Privacy Act (CCPA) and the California Privacy Rights Act (CPRA):
              </Paragraph>
              <BulletList
                items={[
                  <>
                    <strong>Right to Know:</strong> You may request disclosure of the categories and
                    specific pieces of personal information we have collected about you, the sources of
                    that information, the purposes for which it is used, and the categories of third
                    parties with whom it is shared.
                  </>,
                  <>
                    <strong>Right to Delete:</strong> You may request deletion of personal information we
                    have collected about you, subject to certain exceptions.
                  </>,
                  <>
                    <strong>Right to Correct:</strong> You may request correction of inaccurate personal
                    information.
                  </>,
                  <>
                    <strong>Right to Opt Out of Sale or Sharing:</strong> Corner does not sell personal
                    information. We do not share personal information with third parties for cross-context
                    behavioral advertising.
                  </>,
                  <>
                    <strong>Right to Non-Discrimination:</strong> We will not discriminate against you for
                    exercising any of your CCPA rights.
                  </>,
                ]}
              />
              <Paragraph>
                To submit a CCPA request, contact us at privacy@trycorner.co or write to us at the
                address below. We will verify your identity before processing your request.
              </Paragraph>
            </Section>

            <Section id="changes-to-this-privacy-policy" number={13} title="Changes to This Privacy Policy">
              <Paragraph>
                We may update this Privacy Policy from time to time to reflect changes in our practices,
                legal requirements, or the Services. When we make material changes, we will notify you by
                email (to the address on your account) and/or by posting a prominent notice on our
                website. The &quot;Last updated&quot; date at the top of this page will always reflect when the
                most recent revision was made.
              </Paragraph>
              <Paragraph>
                Your continued use of the Services after any changes to this Privacy Policy constitutes
                your acceptance of the updated policy.
              </Paragraph>
            </Section>

            <Section id="how-to-contact-us" number={14} title="How to Contact Us">
              <Paragraph>
                If you have any questions, concerns, or requests related to this Privacy Policy or our
                data practices, please contact us:
              </Paragraph>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  padding: '20px 24px',
                  borderRadius: 20,
                  background: '#FBF7F2',
                  border: '1px solid #E4DDD4',
                }}
              >
                <Paragraph>Corner - operated by Boltzman AI LLC</Paragraph>
                <Paragraph>privacy@trycorner.co</Paragraph>
                <Paragraph>150 Lexington Avenue, Linden, NJ 07036, United States</Paragraph>
              </div>
              <Paragraph>
                For data deletion requests, account removal, or to exercise any of your privacy rights,
                email us at privacy@trycorner.co with the subject line &quot;Privacy Request&quot; and we will
                respond within 30 days.
              </Paragraph>
              <Paragraph>Corner is a product of Boltzman AI LLC. All rights reserved.</Paragraph>
            </Section>
          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  )
}

const tableHeadingStyle = {
  textAlign: 'left' as const,
  padding: '18px 20px',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  color: '#6A5745',
  borderBottom: '1px solid #E4DDD4',
  fontFamily: font,
}

const tableCellStyle = {
  padding: '18px 20px',
  fontSize: 15,
  lineHeight: 1.65,
  color: '#3B3128',
  borderBottom: '1px solid #F0E9E0',
  verticalAlign: 'top' as const,
  fontFamily: font,
}

const tableCellPrimaryStyle = {
  ...tableCellStyle,
  color: '#1A1714',
  fontWeight: 600,
}
